import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { changePassword } from "../../services/authApi";
import { getAccessToken } from "../../services/tokenService";
import { formatValidationError } from "../../utils/formatValidation";
import { formatPasswordNotStrongEnough } from "../../utils/passwordPolicy";
import PasswordStrengthHints from "./PasswordStrengthHints";

const ChangePasswordDialog = ({ open, onClose }) => {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setError("");
      setShowCurrent(false);
      setShowNew(false);
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError("");

    if (!currentPw || !newPw || !confirmPw) {
      setError("Please fill all fields.");
      return;
    }
    const newPwPolicyErr = formatPasswordNotStrongEnough(newPw);
    if (newPwPolicyErr) {
      setError(newPwPolicyErr);
      return;
    }
    if (newPw !== confirmPw) {
      setError("New passwords do not match.");
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setError("You are not signed in.");
      return;
    }

    setLoading(true);
    try {
      await changePassword(
        { current_password: currentPw, new_password: newPw },
        token
      );
      setSuccess("Password changed successfully.");
      onClose && onClose();
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Unable to change password."));
    } finally {
      setLoading(false);
    }
  };

  const adornment = (visible, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={toggle}
          onMouseDown={(e) => e.preventDefault()}
          edge="end"
        >
          {visible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
            /* `overflow: hidden` clips MUI outlined labels when they float above the first field */
            overflow: "visible",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pr: 1,
            bgcolor: "background.paper",
          }}
        >
          <DialogTitle sx={{ flexGrow: 1, fontWeight: 700, pb: 1.5 }}>
            Change Password
          </DialogTitle>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            pt: 2,
            overflow: "visible",
          }}
        >
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          <Stack
            component="form"
            spacing={2}
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            sx={{
              "& .MuiInputBase-input::placeholder": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              /* Room for outlined “Current Password” label when shrunk (was clipping / tucking under title) */
              "& .MuiTextField-root:first-of-type": { mt: 0.75 },
            }}
          >
            <TextField
              label="Current Password"
              type={showCurrent ? "text" : "password"}
              fullWidth
              size="small"
              required
              InputProps={adornment(showCurrent, () => setShowCurrent((s) => !s))}
              value={currentPw}
              onChange={(e) => { setCurrentPw(e.target.value); setError(""); }}
            />
            <TextField
              label="New Password"
              type={showNew ? "text" : "password"}
              fullWidth
              size="small"
              required
              InputProps={adornment(showNew, () => setShowNew((s) => !s))}
              value={newPw}
              onChange={(e) => { setNewPw(e.target.value); setError(""); }}
            />
            <PasswordStrengthHints password={newPw} sx={{ mt: -0.5 }} />
            <TextField
              label="Confirm New Password"
              type={showNew ? "text" : "password"}
              fullWidth
              size="small"
              required
              value={confirmPw}
              onChange={(e) => { setConfirmPw(e.target.value); setError(""); }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {loading ? "Updating..." : "Change Password"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePasswordDialog;
